import { Button } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";
import Loading from "../../../../core/common/custom-loading/Loading";

interface CaseActionItemsProps {
  assignmentID?: string;
  onSuccess?: () => void;
}

const CheckListTable: React.FC<CaseActionItemsProps> = ({
  assignmentID,
  onSuccess,
}) => {
  const [allChecksList, setAllChecksList] = useState<any[]>([]);
  const [checkName, setCheckName] = useState<any[]>([]);

  const getChecksHistoryAPI = useApi<any>(
    assignmentID
      ? `/api/v1/candidate/forms/case-check-summary/${assignmentID}`
      : "",
    {
      method: "GET",
      autoCall: true,
      handleGlobalError: false,
    },
  );

  const getCases = useApi<any>(
    assignmentID ? `/api/v1/admin/checks-details/${assignmentID}` : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const submitChecksApi = useApi<any>(webservice_url.admin_addMoreChecksAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  useEffect(() => {
    const fetchCheckHistory = async () => {
      try {
        const res = await getChecksHistoryAPI.callApi();
        if (res.success) {
          setCheckName(res?.data?.checks);
        }
      } catch (err: any) {
        console.error(err);
      }
    };

    fetchCheckHistory();
  }, [assignmentID]);

  const fetchCasesHistory = async () => {
    if (!assignmentID) return;

    try {
      const res = await getCases.callApi();

      const formattedData =
        res?.data?.map((group: any) => ({
          category: group.header_name,
          checks: group.checks.map((check: any) => ({
            check_id: check.check_id,
            label: check.check_name,
            value: check.check_id,
            description: check.check_desc,
            price: check.price,
            status: check.status,
          })),
        })) || [];

      setAllChecksList(formattedData);
    } catch (error) {
      console.error(error);
      setAllChecksList([]);
    }
  };

  useEffect(() => {
    if (assignmentID) {
      fetchCasesHistory();
    }
  }, [assignmentID]);

  const toggleCheck = useCallback((checkId: string) => {
    setAllChecksList((prev) =>
      prev.map((category) => ({
        ...category,
        checks: category.checks.map((check: any) =>
          check.check_id === checkId
            ? {
                ...check,
                selected: !check.selected,
              }
            : check,
        ),
      })),
    );
  }, []);

  const selectedCheckList = useMemo(
    () =>
      allChecksList.flatMap((cat) => cat.checks.filter((c: any) => c.selected)),
    [allChecksList],
  );

  const totalSum = useMemo(
    () =>
      selectedCheckList.reduce(
        (sum: number, item: any) => sum + Number(item.price || 0),
        0,
      ),
    [selectedCheckList],
  );

  const handleSubmitOrder = async () => {
    if (!assignmentID) return;

    const payload = {
      assignment_id: assignmentID,
      check_ids: selectedCheckList.map((item: any) => item.check_id),
    };

    try {
      const res = await submitChecksApi.callApi(payload);
      console.log(res);
      fetchCasesHistory();
      await onSuccess?.();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Unable to submit order", "error");
    }
  };

  const hasEmploymentCheck = checkName.some(
    (item: any) =>
      item?.check_name?.startsWith("Employment") ||
      item?.check_name?.startsWith("Employement"),
  );

  const loader = getCases.loading;

  return (
    <>
      <style>{`
        .clt-card {
          border: 1px solid #eef0f3;
          border-radius: 14px;
          box-shadow: 0 2px 10px rgba(20, 20, 43, 0.04);
          overflow: hidden;
          transition: box-shadow 0.2s ease;
        }
        .clt-card:hover {
          box-shadow: 0 6px 18px rgba(20, 20, 43, 0.07);
        }
        .clt-header {
          background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
          border-bottom: 1px solid #eef0f3;
        }
        .clt-step-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background-color: #eef2ff;
          color: #4338ca;
          font-size: 0.8rem;
          font-weight: 700;
          margin-right: 10px;
          flex-shrink: 0;
        }
        .clt-table thead th,
        .clt-table tbody tr.clt-group-row th {
          font-size: 0.78rem;
          letter-spacing: 0.03em;
        }
        .clt-group-row th {
          background-color: #1f2430 !important;
          border-color: #1f2430 !important;
        }
        .clt-check-row {
          transition: background-color 0.15s ease;
        }
        .clt-check-row:hover {
          background-color: #f8f9fc;
        }
        .clt-check-row td {
          vertical-align: middle;
          padding-top: 0.7rem;
          padding-bottom: 0.7rem;
        }
        .clt-form-check-input {
          width: 1.1em;
          height: 1.1em;
          cursor: pointer;
          border: 1.5px solid #c7cbd4;
        }
        .clt-form-check-input:checked {
          background-color: #4338ca;
          border-color: #4338ca;
        }
        .clt-check-label {
          cursor: pointer;
          font-size: 0.92rem;
          color: #2b2f38;
        }
        .clt-price-pill {
          display: inline-block;
          min-width: 64px;
          padding: 0.25rem 0.6rem;
          background-color: #f4f5f8;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.85rem;
          color: #3a3f4b;
        }
        .clt-summary-list {
          list-style: none;
        }
        .clt-summary-item {
          border-bottom: 1px dashed #eceef2;
          transition: background-color 0.15s ease;
        }
        .clt-summary-item:last-of-type {
          border-bottom: none;
        }
        .clt-summary-item:hover {
          background-color: #fafbfc;
        }
        .clt-summary-index {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background-color: #eef2ff;
          color: #4338ca;
          font-size: 0.72rem;
          font-weight: 700;
          margin-right: 10px;
          flex-shrink: 0;
        }
        .clt-empty-state {
          color: #9098a8;
          font-size: 0.88rem;
          font-style: italic;
        }
        .clt-total-row {
          background-color: #f8f9fc;
          border-radius: 10px;
          margin-top: 0.5rem;
        }
        .clt-total-price {
          font-size: 1.05rem;
          color: #4338ca;
        }
      `}</style>

      {loader ? (
        <Loading />
      ) : (
        <div className="card clt-card mt-4">
          <div className="clt-header d-flex align-items-center px-3 py-3">
            <span className="clt-step-badge">3</span>
            <span className="fw-semibold text-dark">
              Select the checks and quantity
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered mb-0 clt-table">
              <tbody>
                {allChecksList?.map((group) => (
                  <React.Fragment key={group.category}>
                    <tr className="clt-group-row">
                      <th className="bg-dark text-white text-uppercase">
                        {group.category}
                      </th>
                      {/* <th className="text-center bg-dark text-white text-uppercase">
                        Price
                      </th> */}
                    </tr>

                    {group.checks.map((check: any) => {
                      const isEmploymentCheck =
                        check?.label?.startsWith("Employment") ||
                        check?.label?.startsWith("Employement");

                      const isDisabled =
                        hasEmploymentCheck && isEmploymentCheck;

                      return (
                        <tr key={check.check_id} className="clt-check-row">
                          <td>
                            <div className="py-1 d-flex align-items-center">
                              <input
                                className="form-check-input clt-form-check-input"
                                type="checkbox"
                                id={check.check_id}
                                checked={check.selected}
                                disabled={isDisabled}
                                onChange={() => toggleCheck(check.check_id)}
                              />

                              <label
                                className="form-check-label clt-check-label ms-2 mb-0"
                                htmlFor={check.check_id}
                                style={{
                                  opacity: isDisabled ? 0.6 : 1,
                                  cursor: isDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                {check.label}
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card clt-card mt-4 pb-4">
        <div className="clt-header d-flex align-items-center px-3 py-3">
          <span className="clt-step-badge">4</span>
          <span className="fw-semibold text-dark">
            You have added these checks
          </span>
        </div>

        <ul className="clt-summary-list py-2 px-3 mb-0">
          {selectedCheckList?.length ? (
            selectedCheckList.map((check, idx) => (
              <li
                key={check.check_id}
                className="clt-summary-item py-2 px-2 d-flex align-items-center justify-content-between"
                style={{ maxWidth: "600px" }}
              >
                <div className="d-flex align-items-center">
                  <span className="clt-summary-index">{idx + 1}</span>
                  <span className="text-dark">{check.label}</span>
                </div>
                {/* <span className="clt-price-pill">{check.price}</span> */}
              </li>
            ))
          ) : (
            <li className="py-2 px-2 clt-empty-state">
              No checks selected yet.
            </li>
          )}

          {/* <li
            className="clt-total-row py-2 px-3 d-flex align-items-center justify-content-between fw-semibold text-dark mt-2"
            style={{ maxWidth: "600px" }}
          >
            <div>Total Price</div>
            <div className="clt-total-price">{totalSum}</div>
          </li> */}
        </ul>

        <div className="d-flex justify-content-end align-items-center gap-2 px-3 mt-4">
          <button
            className="btn btn-primary ac-add-check-btn d-inline-flex align-items-center"
            onClick={handleSubmitOrder}
            disabled={!selectedCheckList.length}
          >
            Submit Order
          </button>
        </div>
      </div>
    </>
  );
};

export default CheckListTable;
